<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  DialogRoot,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from 'reka-ui'
import { X, CircleCheck, CircleAlert } from 'lucide-vue-next'
import { Button } from '@/lib/ui/button'
import { Textarea } from '@/lib/ui/textarea'
import { FeedbackApi } from '@/api/feedback'
import { FEEDBACK_CONFIG } from '@/types/constants'
import { logger } from '@/utils/logger'
import { cn } from '@/lib/ui/utils'
import type { IFeedbackDialogProps, IFeedbackDialogEmits } from './types'

const props = withDefaults(defineProps<IFeedbackDialogProps>(), {
  appVersion: 'unknown',
})
const emit = defineEmits<IFeedbackDialogEmits>()

type FeedbackView = 'form' | 'success'

const selectedIndex = ref<number | null>(null)
const message = ref('')
const currentView = ref<FeedbackView>('form')
const isSubmitting = ref(false)
const submitError = ref('')

const selectedOption = computed(() =>
  selectedIndex.value !== null
    ? FEEDBACK_CONFIG.CATEGORIES[selectedIndex.value]
    : null
)
const isSubmitDisabled = computed(
  () => selectedIndex.value === null || isSubmitting.value
)

function resetForm(): void {
  selectedIndex.value = null
  message.value = ''
  currentView.value = 'form'
  isSubmitting.value = false
  submitError.value = ''
}

watch(
  () => props.open,
  open => {
    if (open) resetForm()
  }
)

function handleClose(): void {
  emit('update:open', false)
}

async function handleSubmit(): Promise<void> {
  if (isSubmitDisabled.value || !selectedOption.value) return

  isSubmitting.value = true
  submitError.value = ''

  try {
    await FeedbackApi.submitFeedback({
      Category: selectedOption.value.apiCategory,
      Subject: selectedOption.value.label,
      Message: message.value.trim() || selectedOption.value.label,
      AppVersion: props.appVersion,
      username: props.userName ?? undefined,
    })

    currentView.value = 'success'
    logger.info(
      'Feedback submitted',
      {
        category: selectedOption.value.apiCategory,
        subject: selectedOption.value.label,
      },
      { component: 'FeedbackDialog' }
    )
  } catch (error) {
    submitError.value = 'Failed to submit feedback. Please try again.'
    logger.error(
      'Feedback submission failed',
      error instanceof Error ? error : new Error(String(error)),
      { component: 'FeedbackDialog' }
    )
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <DialogRoot :open="open" @update:open="emit('update:open', $event)">
    <DialogOverlay
      class="fixed inset-0 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      style="z-index: calc(var(--widget-z-index) + 3)"
    />
    <DialogContent
      class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background border rounded-lg shadow-lg flex flex-col outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
      style="z-index: calc(var(--widget-z-index) + 4)"
    >
      <!-- Form view -->
      <template v-if="currentView === 'form'">
        <div class="flex items-center justify-between p-5 pb-0">
          <DialogTitle class="text-base font-semibold text-foreground">
            Share Feedback
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            class="size-8 shrink-0 -mr-1.5 text-muted-foreground hover:text-foreground"
            aria-label="Close"
            @click="handleClose"
          >
            <X class="size-4" />
          </Button>
        </div>

        <DialogDescription class="px-5 pt-1 text-sm text-muted-foreground">
          Help us improve by sharing your experience.
        </DialogDescription>

        <div class="flex flex-col gap-4 p-5">
          <!-- Category selection -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-foreground">
              What's your feedback about?
            </span>
            <div class="flex flex-col gap-1.5">
              <div
                v-for="(category, index) in FEEDBACK_CONFIG.CATEGORIES"
                :key="category.label"
                role="radio"
                :aria-checked="selectedIndex === index"
                tabindex="0"
                :class="
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors select-none',
                    selectedIndex === index
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted'
                  )
                "
                @click="selectedIndex = index"
                @keydown.enter="selectedIndex = index"
                @keydown.space.prevent="selectedIndex = index"
              >
                <div
                  :class="
                    cn(
                      'size-4 rounded-full border-2 flex items-center justify-center shrink-0',
                      selectedIndex === index
                        ? 'border-primary'
                        : 'border-muted-foreground/40'
                    )
                  "
                >
                  <div
                    v-if="selectedIndex === index"
                    class="size-2 rounded-full bg-primary"
                  />
                </div>
                <span class="text-sm text-foreground">{{
                  category.label
                }}</span>
              </div>
            </div>
          </div>

          <!-- Description textarea -->
          <div class="flex flex-col gap-1.5">
            <label
              for="feedback-message"
              class="text-sm font-medium text-foreground"
            >
              Tell us more
              <span class="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="feedback-message"
              v-model="message"
              placeholder="Describe your experience or suggestion..."
              class="min-h-24 resize-none"
            />
          </div>

          <!-- Error message -->
          <div
            v-if="submitError"
            class="flex items-center gap-2 text-sm text-destructive"
          >
            <CircleAlert class="size-4 shrink-0" />
            <span>{{ submitError }}</span>
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-2 pt-1">
            <Button variant="ghost" @click="handleClose">Cancel</Button>
            <Button :disabled="isSubmitDisabled" @click="handleSubmit">
              {{ isSubmitting ? 'Submitting...' : 'Submit Feedback' }}
            </Button>
          </div>
        </div>
      </template>

      <!-- Success view -->
      <template v-else>
        <div class="flex flex-col items-center gap-3 p-8 text-center">
          <div
            class="size-12 rounded-full bg-primary/10 flex items-center justify-center"
          >
            <CircleCheck class="size-6 text-primary" />
          </div>
          <DialogTitle class="text-base font-semibold text-foreground">
            Thank you!
          </DialogTitle>
          <DialogDescription class="text-sm text-muted-foreground">
            Your feedback has been submitted. We appreciate you helping us
            improve.
          </DialogDescription>
          <Button class="mt-2" @click="handleClose">Close</Button>
        </div>
      </template>
    </DialogContent>
  </DialogRoot>
</template>
